import type {
  AppEvent, MoneyModelRecord, PurchaseTemplateRecord, RecurrencePeriod,
} from '../core/types.js';

export function currencySymbol(code: string | undefined): string {
  switch (code) {
    case 'EUR': return '€';
    case 'GBP': return '£';
    case 'JPY': return '¥';
    case 'INR': return '₹';
    case 'BRL': return 'R$';
    case 'MXN': return 'MX$';
    case 'CAD': return 'C$';
    case 'AUD': return 'A$';
    default: return '$';
  }
}

export interface UsageRate {
  /** Average uses per week implied by the baseline (used to convert period spend → per-use). */
  baselineUsesPerWeek?: number | undefined;
}

/** Resolve a per-use cost given a money model. Returns 0 if unresolvable. */
export function costPerUse(m: MoneyModelRecord | undefined, rate: UsageRate = {}): number {
  if (!m || !m.enabled) return 0;
  if (m.estimationType === 'per_use') return m.costPerUse ?? 0;
  if (m.estimationType === 'per_pack') {
    if (!m.purchaseCost || !m.unitsPerPurchase || m.unitsPerPurchase <= 0) return 0;
    return m.purchaseCost / m.unitsPerPurchase;
  }
  const usesPerWeek = rate.baselineUsesPerWeek ?? 0;
  if (usesPerWeek <= 0) return 0;
  if (m.estimationType === 'weekly_spend' && m.estimatedSpendPerPeriod) return m.estimatedSpendPerPeriod / usesPerWeek;
  if (m.estimationType === 'monthly_spend' && m.estimatedSpendPerPeriod) return m.estimatedSpendPerPeriod / (usesPerWeek * 4.33);
  return m.costPerUse ?? 0;
}

export function fmtMoney(amount: number, currency: string | undefined = 'USD'): string {
  const sym = currencySymbol(currency);
  return `${sym}${amount.toFixed(2)}`;
}

export interface SkippedPurchaseTotals {
  count: number;
  totalProtected: number;
  currency: string;
}

/**
 * Sum success_logged events with kind=skipped_purchase. Filters undone events.
 * Defaults currency to the first event's currency or USD when none provided.
 */
/** Normalize a recurrence to monthly. */
export function monthlyAmount(amount: number, recurrence: RecurrencePeriod | undefined): number {
  switch (recurrence) {
    case 'weekly': return amount * (52 / 12);
    case 'monthly': return amount;
    case 'yearly': return amount / 12;
    default: return 0;
  }
}

export interface SubscriptionTotals {
  /** Active monthly cost summed across non-canceled subscriptions. */
  activeMonthlyCost: number;
  /** Estimated monthly protected from canceled subscriptions. */
  canceledMonthlyProtected: number;
  currency: string;
}

export function totalSubscriptions(
  templates: PurchaseTemplateRecord[],
  preferredCurrency: string = 'USD',
): SubscriptionTotals {
  let active = 0;
  let canceled = 0;
  let currency = preferredCurrency;
  for (const t of templates) {
    if (t.kind !== 'subscription' || !t.recurringAmount) continue;
    if (typeof t.currency === 'string') currency = t.currency;
    const monthly = monthlyAmount(t.recurringAmount, t.recurrence ?? 'monthly');
    if (t.canceledAt) canceled += monthly;
    else if (t.active) active += monthly;
  }
  return { activeMonthlyCost: active, canceledMonthlyProtected: canceled, currency };
}

export interface SpendingCapStatus {
  cap: number;
  period: 'week' | 'month';
  used: number;
  remaining: number;
  warnAtPct: number;
  state: 'under' | 'warn' | 'over';
  currency: string;
}

/**
 * Compute spending-cap status from money-model and purchase events within
 * the current cap period. Used in gambling/shopping/gaming flows.
 */
export function spendingCapStatus(
  money: MoneyModelRecord | undefined,
  events: AppEvent[],
  nowMs: number = Date.now(),
): SpendingCapStatus | null {
  if (!money?.spendingCap || !money.spendingCapPeriod) return null;
  const periodMs = money.spendingCapPeriod === 'week' ? 7 * 86400000 : 30 * 86400000;
  const fromMs = nowMs - periodMs;
  let used = 0;
  for (const e of events) {
    if (e.status === 'undone') continue;
    if (e.type !== 'purchase_logged') continue;
    if (new Date(e.occurredAt).getTime() < fromMs) continue;
    const p = e.payload as { amount?: number };
    if (typeof p.amount === 'number') used += p.amount;
  }
  const warnAtPct = money.spendingCapWarnPct ?? 80;
  const ratio = money.spendingCap > 0 ? (used / money.spendingCap) * 100 : 0;
  const state: SpendingCapStatus['state'] =
    used >= money.spendingCap ? 'over' : ratio >= warnAtPct ? 'warn' : 'under';
  return {
    cap: money.spendingCap,
    period: money.spendingCapPeriod,
    used,
    remaining: Math.max(0, money.spendingCap - used),
    warnAtPct,
    state,
    currency: money.currency,
  };
}

export function totalSkippedPurchase(events: AppEvent[], fromIso?: string): SkippedPurchaseTotals {
  let count = 0;
  let totalProtected = 0;
  let currency = 'USD';
  for (const e of events) {
    if (e.status === 'undone') continue;
    if (e.type !== 'success_logged') continue;
    const p = e.payload as { kind?: string; amountAvoided?: number; currency?: string };
    if (p.kind !== 'skipped_purchase') continue;
    if (fromIso && e.occurredAt < fromIso) continue;
    count += 1;
    totalProtected += typeof p.amountAvoided === 'number' ? p.amountAvoided : 0;
    if (typeof p.currency === 'string') currency = p.currency;
  }
  return { count, totalProtected, currency };
}
